import {
  AbilityBuilder,
  createMongoAbility,
  type MongoAbility,
} from "@casl/ability";

export type SystemRole = "super_admin" | "admin" | "billing" | "user";

export type EndUserRole = "salesperson" | "crew_chief" | "manager";

export type Actions =
  | "manage"
  | "read"
  | "create"
  | "update"
  | "delete"
  | "reset"
  | "backup"
  | "restore"
  | "impersonate"
  | "revoke"
  | "configure";

interface BusinessFields {
  id?: string;
  account_id?: string;
}
interface AccountFields {
  id?: string;
}
interface UserFields {
  id?: string;
  account_id?: string;
}
interface BusinessScopedFields {
  id?: string;
  business_id?: string;
  account_id?: string;
}
interface AccountScopedFields {
  id?: string;
  account_id?: string;
}
// Business-scoped fields plus the ADV-892 assignment columns. The optional
// salesperson_user_id / crew_chief_user_id let CASL rule conditions match on
// "you can only update opportunities where you're the assigned salesperson".
interface OwnedBusinessScopedFields {
  id?: string;
  business_id?: string;
  account_id?: string;
  salesperson_user_id?: string | null;
  crew_chief_user_id?: string | null;
}

// Subjects union: each subject is either the string name OR an object tagged
// with __caslSubjectType__ (what the runtime `subject()` helper produces).
// Including the field shape lets CASL infer `MongoQuery<Shape>` for conditions
// in both the builder (`can("read", "Business", { account_id })`) and at check
// time (`ability.can("read", subject("Business", { account_id }))`).
type Tagged<TName extends string, TShape> = TShape & {
  readonly __caslSubjectType__?: TName;
};

export type Subjects =
  | "all"
  | "Business"
  | Tagged<"Business", BusinessFields>
  | "Account"
  | Tagged<"Account", AccountFields>
  | "User"
  | Tagged<"User", UserFields>
  | "Job"
  | Tagged<"Job", OwnedBusinessScopedFields>
  | "Customer"
  | Tagged<"Customer", OwnedBusinessScopedFields>
  // ADV-1101: contact-identity edits (email/phone/name/company) on the
  // unified core.contacts row, separate from the customer-attributes
  // surface gated by `Customer`. Granted to the same roles that today have
  // `update Customer` so the existing operator population can resolve
  // unresolved imported values without a permission change.
  | "Contact"
  | Tagged<"Contact", BusinessScopedFields>
  | "Invoice"
  | Tagged<"Invoice", OwnedBusinessScopedFields>
  | "Transaction"
  | Tagged<"Transaction", OwnedBusinessScopedFields>
  | "Warranty"
  | Tagged<"Warranty", BusinessScopedFields>
  // ADV-1170: warranty 3-layer model (ADR-0047). Term = template (account-scoped
  // configuration), Warranty above = obligation (business-scoped), Claim =
  // remediation work (business-scoped, was the ADV-1144 shape).
  | "WarrantyTerm"
  | Tagged<"WarrantyTerm", AccountScopedFields>
  | "WarrantyClaim"
  | Tagged<"WarrantyClaim", BusinessScopedFields>
  // ADV-1176 / ADR-0048: service/maintenance agreements (sibling of warranties).
  // Plan = reusable template (account-scoped configuration), Agreement = the
  // per-customer recurring instance (business-scoped operational record).
  | "ServicePlan"
  | Tagged<"ServicePlan", AccountScopedFields>
  | "ServiceAgreement"
  | Tagged<"ServiceAgreement", BusinessScopedFields>
  | "Commission"
  | Tagged<"Commission", BusinessScopedFields>
  | "PaymentMethod"
  | Tagged<"PaymentMethod", AccountScopedFields>
  | "Subscription"
  | Tagged<"Subscription", AccountScopedFields>
  | "ApiKey"
  | Tagged<"ApiKey", AccountScopedFields>
  | "Integration"
  | Tagged<"Integration", AccountScopedFields>
  | "AuditLog"
  | Tagged<"AuditLog", AccountScopedFields>
  | "Opportunity"
  | Tagged<"Opportunity", OwnedBusinessScopedFields>
  | "Proposal"
  | Tagged<"Proposal", OwnedBusinessScopedFields>
  | "TeamMember"
  | Tagged<"TeamMember", BusinessScopedFields>
  | "Brand"
  | Tagged<"Brand", BusinessScopedFields>
  | "Crew"
  | Tagged<"Crew", BusinessScopedFields>
  | "CommissionPlan"
  | Tagged<"CommissionPlan", AccountScopedFields>
  | "ActivityEvent"
  | Tagged<"ActivityEvent", BusinessScopedFields>
  | "Appointment"
  | Tagged<"Appointment", BusinessScopedFields>
  | "Import"
  | Tagged<"Import", AccountScopedFields>
  // ADV-1275: a disposable demo/training account the user owns. Account-scoped
  // because creation force-links the caller as admin and teardown is a full
  // hard-delete of that account; admins create/read/delete their own demo (a
  // peer account they own), super_admin covered by `manage all`.
  | "DemoAccount"
  | Tagged<"DemoAccount", AccountScopedFields>
  | "WebhookEndpoint"
  | Tagged<"WebhookEndpoint", BusinessScopedFields>;

export type AppAbility = MongoAbility<[Actions, Subjects]>;

export interface AbilityContext {
  systemRole: SystemRole;
  endUserRole?: EndUserRole | null;
  accountId: string;
  businessId?: string | null;
  userId?: string | null;
}

export function defineAbilityFor(ctx: AbilityContext): AppAbility {
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(
    createMongoAbility,
  );

  if (ctx.systemRole === "super_admin") {
    can("manage", "all");
    // Ability-layer guard against chained impersonation; path-layer guard in auth.ts.
    cannot("impersonate", "User");
    return build();
  }

  if (ctx.systemRole === "admin") {
    can(["read", "update"], "Account", { id: ctx.accountId });
    can(["read", "create", "update", "delete"], "Business", {
      account_id: ctx.accountId,
    });
    can(["reset", "backup", "restore"], "Business", {
      account_id: ctx.accountId,
    });
    can(["read", "create", "update", "delete"], "User", {
      account_id: ctx.accountId,
    });
    can(["read", "create", "update", "delete"], "Job", {
      account_id: ctx.accountId,
    });
    can(["read", "create", "update", "delete"], "Customer", {
      account_id: ctx.accountId,
    });
    // ADV-1101: Contact mirrors Customer at the admin tier — account-scoped
    // CRUD over the unified contact-identity surface.
    can(["read", "create", "update", "delete"], "Contact", {
      account_id: ctx.accountId,
    });
    can(["read", "create", "revoke"], "ApiKey", { account_id: ctx.accountId });
    can(["read", "configure"], "Integration", { account_id: ctx.accountId });
    can(["read", "create", "update", "delete"], "Invoice", {
      account_id: ctx.accountId,
    });
    // ADV-1145: Transaction is the imported expense/payment ledger — same
    // sensitivity tier as Invoice (admin full CRUD, account-scoped).
    can(["read", "create", "update", "delete"], "Transaction", {
      account_id: ctx.accountId,
    });
    // ADV-1144: warranties are business-scoped operational records with no
    // per-user owner column — read-only at the API tier for now.
    can("read", "Warranty", { account_id: ctx.accountId });
    // ADV-1170 / ADR-0047: terms are reusable coverage templates (account-
    // scoped configuration), warranties (above) are obligations, and claims
    // are the remediation work renamed from the original ADV-1144 shape.
    can("manage", "WarrantyTerm", { account_id: ctx.accountId });
    can("manage", "Warranty", { account_id: ctx.accountId });
    can("read", "WarrantyClaim", { account_id: ctx.accountId });
    // ADV-1176 / ADR-0048: service plans are reusable templates (account-scoped
    // configuration), service agreements are the per-customer recurring
    // instances. Admins manage both, mirroring the warranty term/obligation tier.
    can("manage", "ServicePlan", { account_id: ctx.accountId });
    can("manage", "ServiceAgreement", { account_id: ctx.accountId });
    // ADV-1147: commissions are business-scoped, read-only at the API tier
    // (recipient is team_member_role_id, not an owning user). Payroll, not
    // SaaS billing — billing role is intentionally not granted read.
    can("read", "Commission", { account_id: ctx.accountId });
    can("read", "PaymentMethod", { account_id: ctx.accountId });
    can("read", "Subscription", { account_id: ctx.accountId });
    can("read", "AuditLog", { account_id: ctx.accountId });
    can(["read", "create", "update", "delete"], "Opportunity", {
      account_id: ctx.accountId,
    });
    can(["read", "create", "update", "delete"], "Proposal", {
      account_id: ctx.accountId,
    });
    can(["read", "create", "update", "delete"], "TeamMember", {
      account_id: ctx.accountId,
    });
    can(["read", "create", "update", "delete"], "Brand", {
      account_id: ctx.accountId,
    });
    can(["read", "create", "update", "delete"], "Crew", {
      account_id: ctx.accountId,
    });
    can("manage", "CommissionPlan", { account_id: ctx.accountId });
    can("read", "ActivityEvent", { account_id: ctx.accountId });
    can(["read", "create", "update", "delete"], "Appointment", {
      account_id: ctx.accountId,
    });
    can("manage", "Import", { account_id: ctx.accountId });
    can("manage", "WebhookEndpoint", { account_id: ctx.accountId });
    // ADV-1275: a demo account is a NEW peer account the admin force-links
    // themselves into, not a record within their current account — so the
    // create/read/delete grants are unconditional (no `account_id` condition),
    // matching the way the create/teardown flows operate on a separate tenant.
    // super_admin gets this via `manage all`; billing/user are intentionally
    // not granted (creating/deleting an entire account is an admin action).
    can(["create", "read", "delete"], "DemoAccount");
    cannot("impersonate", "User");
    return build();
  }

  if (ctx.systemRole === "billing") {
    can("read", "Invoice", { account_id: ctx.accountId });
    // ADV-1145: billing reads the financial ledger alongside invoices.
    can("read", "Transaction", { account_id: ctx.accountId });
    can("read", "PaymentMethod", { account_id: ctx.accountId });
    can("read", "Subscription", { account_id: ctx.accountId });
    can("update", "PaymentMethod", { account_id: ctx.accountId });
    can("read", "Business", { account_id: ctx.accountId });
    can("read", "Job", { account_id: ctx.accountId });
    can("read", "Customer", { account_id: ctx.accountId });
    // ADV-1101: billing reads contact identity (read-only, same tier as Customer).
    can("read", "Contact", { account_id: ctx.accountId });
    return build();
  }

  if (ctx.systemRole === "user") {
    if (ctx.businessId) {
      // ActivityEvent: all user-tier roles can read the business activity feed.
      // Billing has its own block above and never enters the "user" branch.
      can("read", "ActivityEvent", { business_id: ctx.businessId });
      // ADV-1144: warranties are business-scoped operational records (no owner
      // column). Like ActivityEvent, every business member can read them; there
      // is no ownership-scoped variant to apply.
      can("read", "Warranty", { business_id: ctx.businessId });
      // ADV-1170 / ADR-0047: terms are account-scoped templates (configuration
      // reads are appropriate at the user tier); claims are business-scoped
      // remediation work, read-only at the user tier (mirrors Warranty).
      can("read", "WarrantyTerm", { account_id: ctx.accountId });
      can("read", "WarrantyClaim", { business_id: ctx.businessId });
      // ADV-1176 / ADR-0048: plans are account-scoped templates (configuration
      // reads appropriate at the user tier); agreements are business-scoped
      // operational records every business member can read (mirrors Warranty).
      can("read", "ServicePlan", { account_id: ctx.accountId });
      can("read", "ServiceAgreement", { business_id: ctx.businessId });
      // ADV-1147: commissions are business-scoped operational records (recipient
      // is team_member_role_id, not an owning user). Like Warranty, every
      // business member can read them; no ownership-scoped variant applies.
      can("read", "Commission", { business_id: ctx.businessId });

      // ADV-948: baseline read applies only when endUserRole is absent (JWT
      // fallback) or "manager" (full business read). Salesperson and crew_chief
      // get explicit ownership-scoped reads further down — granting an
      // unconditional baseline here would defeat that scoping for them.
      const grantBaselineReads =
        !ctx.endUserRole || ctx.endUserRole === "manager";
      if (grantBaselineReads) {
        can("read", "Job", { business_id: ctx.businessId });
        can("read", "Customer", { business_id: ctx.businessId });
        // ADV-1101: contact-identity reads track the Customer baseline.
        can("read", "Contact", { business_id: ctx.businessId });
        can("read", "Opportunity", { business_id: ctx.businessId });
        can("read", "Proposal", { business_id: ctx.businessId });
      }

      // ADV-892: end-user roles split into ownership-scoped rules. Manager
      // keeps the wholesale grant; salesperson and crew_chief now scope to
      // assigned records via the assignment columns landed in the same
      // migration (salesperson_user_id, crew_chief_user_id).
      if (ctx.endUserRole === "manager") {
        // Manager — full business CRUD (unchanged from pre-ADV-892).
        // Note: Invoice visibility stays admin/billing-only at the user tier;
        // only salesperson gets a scoped read for commission attribution.
        can("manage", "WebhookEndpoint", { business_id: ctx.businessId });
        can(["create", "update", "delete"], "Job", {
          business_id: ctx.businessId,
        });
        can(["create", "update", "delete"], "Customer", {
          business_id: ctx.businessId,
        });
        // ADV-1101: manager has full business CRUD on contact identity,
        // mirroring the Customer grant immediately above.
        can(["create", "update", "delete"], "Contact", {
          business_id: ctx.businessId,
        });
        can(["create", "update", "delete"], "Opportunity", {
          business_id: ctx.businessId,
        });
        can(["create", "update", "delete"], "Proposal", {
          business_id: ctx.businessId,
        });
        can(["read", "create", "update"], "Appointment", {
          business_id: ctx.businessId,
        });
      }

      if (ctx.endUserRole === "salesperson" && ctx.userId) {
        // Salesperson — own opportunities + everything that hangs off them.
        // Also reads (but not writes) unassigned records so the lead pool
        // stays visible — without this, an unassigned opportunity disappears
        // from the rep's pipeline view.
        can(["read", "create", "update"], "Appointment", {
          business_id: ctx.businessId,
        });
        can("read", "Opportunity", {
          business_id: ctx.businessId,
          salesperson_user_id: ctx.userId,
        });
        can("read", "Opportunity", {
          business_id: ctx.businessId,
          salesperson_user_id: null,
        });
        can(["create", "update", "delete"], "Opportunity", {
          business_id: ctx.businessId,
          salesperson_user_id: ctx.userId,
        });

        can("read", "Proposal", {
          business_id: ctx.businessId,
          salesperson_user_id: ctx.userId,
        });
        can(["create", "update", "delete"], "Proposal", {
          business_id: ctx.businessId,
          salesperson_user_id: ctx.userId,
        });

        can("read", "Job", {
          business_id: ctx.businessId,
          salesperson_user_id: ctx.userId,
        });
        can(["create", "update", "delete"], "Job", {
          business_id: ctx.businessId,
          salesperson_user_id: ctx.userId,
        });

        can("read", "Customer", {
          business_id: ctx.businessId,
          salesperson_user_id: ctx.userId,
        });
        can("read", "Customer", {
          business_id: ctx.businessId,
          salesperson_user_id: null,
        });
        can(["create", "update", "delete"], "Customer", {
          business_id: ctx.businessId,
          salesperson_user_id: ctx.userId,
        });
        // ADV-1101: salesperson edits contact identity on customers they
        // already own. Contact carries no ownership column itself — the
        // service layer joins through contact_roles to enforce the
        // business-scoped read, and the broader manager grant above never
        // fires for an `endUserRole === "salesperson"` request.
        can(["read", "create", "update", "delete"], "Contact", {
          business_id: ctx.businessId,
        });

        // Sales reps see their own invoices for commission attribution.
        can("read", "Invoice", {
          business_id: ctx.businessId,
          salesperson_user_id: ctx.userId,
        });
        // ADV-1145: same scoping for transactions (commission/payment events
        // attributed to the rep via salesperson_user_id, landed in ADV-892).
        can("read", "Transaction", {
          business_id: ctx.businessId,
          salesperson_user_id: ctx.userId,
        });
      }

      if (ctx.endUserRole === "crew_chief" && ctx.userId) {
        // Crew chief — assigned jobs + read-only on the customer behind each
        // job (so they know who to ask about the work). No Opportunity
        // visibility — sales pipeline is invisible to ops.
        can("read", "Job", {
          business_id: ctx.businessId,
          crew_chief_user_id: ctx.userId,
        });
        can("update", "Job", {
          business_id: ctx.businessId,
          crew_chief_user_id: ctx.userId,
        });
        // Crew chief sees any customer within the business (they need to
        // identify the customer behind their assigned jobs without a
        // recursive ability evaluation). Read-only.
        can("read", "Customer", { business_id: ctx.businessId });
        // ADV-1101: crew chief reads contact identity for the same reason
        // they read Customer — identifying the customer behind a job —
        // but writes stay off-limits.
        can("read", "Contact", { business_id: ctx.businessId });
        // Override the baseline Opportunity/Proposal read — crew chief is
        // ops-only and shouldn't see sales pipeline.
        cannot("read", "Opportunity");
        cannot("read", "Proposal");
      }
    }
    return build();
  }

  return build();
}
